<template>
  <form @submit.prevent="onSubmit">
    <slot :dirty="v$.$anyDirty"
          :invalid="v$.$invalid"
          :reset="clearForm" />
  </form>
</template>

<script>
  import useVuelidate from '@vuelidate/core';
  import { cloneDeep } from 'lodash';

  import { accessDeep, objectToPaths } from '@/utilities';

  export default {
    name: 'DynamicForm',
    setup: () => ({ v$: useVuelidate() }),
    props: {
      schema: {
        type: Object,
        required: true,
      },
      defaultValues: {
        type: [Object, undefined],
        required: false,
        default: undefined,
      },
    },
    provide() {
      return {
        form: this,
        updateForm: ({ field, value, trackBy, returnObject, isEditMode }) => {
          // form object to keep track of the form data for vuelidate and the UI components
          //  The submit data object to keep track of the data that will be sent to the server
          const model = accessDeep(this.v$.form, field);
          model.$model = value;
          if (!isEditMode) {
            model.$touch();
          }

          const isSelectInput = !!(typeof value === 'object' && !(value instanceof File) && value);
          let inputValue = value;

          if (isSelectInput && !returnObject) {
            const identifier = trackBy || 'id';

            inputValue = Array.isArray(value) ? value.map((v) => v[identifier]) : value[identifier];
          }

          accessDeep(this.submitData, field, '.', inputValue);
        },
      };
    },
    data() {
      return { form: {}, submitData: {} };
    },
    validations() {
      return { form: cloneDeep(this.schema) };
    },
    watch: {
      defaultValues: {
        handler() {
          this.form = cloneDeep(this.defaultValues || {});
        },
        deep: true,
      },
    },
    methods: {
      clearForm() {
        this.form = cloneDeep(this.defaultValues || {});
        this.submitData = cloneDeep(this.defaultValues || {});
      },
      getData() {
        if (this.defaultValues) {
          this.form = cloneDeep(this.defaultValues || {});
          this.submitData = cloneDeep(this.defaultValues || {});
          return;
        }

        const fields = objectToPaths(this.schema);
        for (const property in fields) {
          accessDeep(this.form, property, '.', '');
        }
      },
      async onSubmit() {
        const isValid = await this.v$.$validate();

        if (!isValid) {
          const errors = this.v$.$errors.map((error) =>
            error.$message.replace(/^(Value|This field)/g, error.$property),
          );
          this.$emit('on-submit-error', errors);
          this.scrollToError();
          return;
        }
        this.$emit('onSubmit', this.submitData);
      },
      scrollToError() {
        const errorElement = document.getElementById(this.v$.$errors[0].$property);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
          });
        }
      },
    },
    created() {
      this.getData();
    },
    mounted() {
      this.$nextTick(() => {
        this.v$.$reset();
      });
    },
  };
</script>
